import {
    format,
    fromUnixTime,
    addHours,
    subHours,
    getDate,
    getMonth,
    getYear,
    isDate
} from 'date-fns';
import { DATE } from '~/modules/customFieldTypes';

// ~/modules/helpers
import { RE } from './regexp';
import { isNumber } from './math';
import { translate } from './lang';
import { capitalizeFirstLetter } from './string';

export const longDateFormat = 'longDate';
export const longDateTimeFormat = 'longDateTime';
export const shortDateFormat = 'shortDate';
export const shortDateTimeFormat = 'shortDateTime';

export const getCurrentUserOffsetAsNum = () => CURRENT_USER?.offset2num ?? 0;

/**
 * Shifts the provided date object by an hour offset.
 * @param {Date} originalDate - The original date object.
 * @param {number} hourOffset - The number of hours to offset, integer.
 * @returns {Date} A new date object offset by the specified number of hours from the original date.
 */
export const shiftDateByHourOffset = (originalDate, hourOffset) => {
    // noinspection UnnecessaryLocalVariableJS
    const date = hourOffset
        ? 0 < hourOffset
            ? addHours(originalDate, hourOffset)
            : subHours(originalDate, -hourOffset) // abs
        : originalDate;
    // console.log({ originalDate, hourOffset, shifted: date });
    return date;
};

/**
 * Converts hours according to the user's timezone offset.
 * @param {(number|string<numeric>)} value
 * @returns {number}
 **/
export const addUserTimezoneOffsetToHours = value => {
    const offset = (CURRENT_USER?.timezone_offset ?? 0) / 1e2;
    const hours = parseInt(value);
    return isNaN(hours) ? offset : offset + hours;
};

/**
 * Converts a given date into its equivalent UTC date.
 * @param {Date} date - The date to convert to UTC.
 * @returns {Date} The equivalent UTC date.
 */
export const toUTCDate = date => {
    // noinspection UnnecessaryLocalVariableJS
    const utcDate =
        date && isDate(date)
            ? new Date(
                  Date.UTC(
                      date.getUTCFullYear(),
                      date.getUTCMonth(),
                      date.getUTCDate(),
                      date.getUTCHours(),
                      date.getUTCMinutes(),
                      date.getUTCSeconds(),
                      date.getUTCMilliseconds()
                  )
              )
            : null;
    // console.log({ date, utcDate });
    return utcDate;
};

/**
 * Compares two timestamps and returns an array containing:
 * - boolean value indicating whether the first timestamp is later than the second.
 * - float value representing the difference between the two timestamps in days.
 * - integer value representing the absolute difference between the two timestamps in milliseconds.
 * @param {number} a - The first timestamp to compare.
 * @param {number} b - The second timestamp to compare.
 * @return {[hasPassed<boolean>, distanceInDays<number>, absoluteDiff<number>]}
 */
export const diffBetweenTimestamps = (a, b) => {
    const hasPassed = a > b;
    const absoluteDiff = hasPassed ? a - b : b - a;
    // 86400000 → (1000 * 60 * 60 * 24) = 1 day
    const distanceInDays = absoluteDiff / 864e5;
    return [hasPassed, distanceInDays, absoluteDiff];
};

/**
 * Get hours-minutes-seconds information about provided value in milliseconds
 * @param ms {milliseconds}
 * @return {{ hours: number, minutes: number, seconds: number }}
 **/
export const getHoursMinutesSeconds = ms => {
    const result = {
        hours: 0,
        minutes: 0,
        seconds: 0
    };
    if (0 < ms) {
        const mod = ms % 36e5;
        const { floor } = Math;
        // 1000 = 1 second
        result.seconds = floor((mod % 6e4) / 1e3);
        // 60000 → (1000 * 60) = 1 minute
        result.minutes = floor(mod / 6e4);
        // 3600000 → (60000 * 60) = 1 hour
        result.hours = floor(ms / 36e5);
    }
    return result;
};

/**
 * Calculates the remaining hours, minutes and seconds to the end, from another or current time.
 * @param {number} end - timestamp in milliseconds.
 * @param {number} [from] - timestamp in milliseconds. Defaults to current time.
 * @returns {(0|{ hours: number, minutes: number, seconds: number })}
 **/
export const getRemainHoursMinutesSecondsTo = (end, from) => {
    const start = from ?? Date.now();
    return start >= end ? 0 : getHoursMinutesSeconds(end - start);
};

/**
 * Returns a MDY string, using the specified separator.
 * @param {date} dateObj
 * @param {string} [separatorString="_"]
 * @return {string}
 **/
export const formatMDY = (dateObj, separatorString) => {
    const separator = (separatorString ?? '-') || '';
    const date = dateObj || new Date();
    return `${
        getMonth(date) + 1 // 𝖬𝖬 1-12
    }${separator}${
        getDate(date) // 𝖽𝖽 1-31
    }${separator}${
        getYear(date) // 𝖸𝖸
    }`;
};

/**
 * Returns a filename based on the given prefix, extension and date
 * matching /^(file|.*)_\d{4}\d{1,2}\d{1,2}_\d{1,2}\d{1,2}.*$/
 * @param {string} [prefix="file"]
 * @param {string} suffix - eg.: file extension
 * @param {Date} [dateObj=new Date()]
 * @returns {string}
 */
export const filenameFromDate = (prefix, suffix, dateObj) => {
    const date = dateObj || new Date();
    return `${
        prefix || 'file' // 𝘥𝘦𝘧𝘢𝘶𝘭𝘵
    }_${
        date.getFullYear() // 𝖸𝖸𝖸𝖸
    }${
        date.getMonth() + 1 // 𝖬𝖬 1-12
    }${
        date.getDate() // 𝖽𝖽 1-31
    }_${
        date.getHours() // 𝗁𝗁 0-24
    }${
        date.getMinutes() // 𝗆𝗆 0-59
    }${
        suffix || '' // 𝘰𝘱𝘵𝘪𝘰𝘯𝘢𝘭
    }`;
};

/**
 * Map containing unit (y|m|d|h|i|s) translation singular and plural messages.
 * @type {Map<string, object>}
 */
const dateUnitTranslationMap = new Map(
    Object.entries({
        y: ['year', 'years'],
        m: ['month', 'months'],
        w: ['week', 'weeks'],
        d: ['day', 'days'],
        h: ['hour', 'hours'],
        i: ['minute', 'minutes'],
        s: ['second', 'seconds']
    }).map(([key, singularAndPlural]) => {
        return [
            key,
            singularAndPlural.map(value => {
                const capitalizedValue = capitalizeFirstLetter(value);
                return {
                    id: value,
                    message: capitalizedValue,
                    defaultMessage: capitalizedValue
                };
            })
        ];
    })
);

/**
 * Format a given time duration into human readable format.
 * @param {number} value - time duration in milliseconds.
 * @param {string} until - time unit limit, range from seconds to weeks.
 * @param {string} separator - between the units of time.
 * @returns {string}
 * @note 𝗧𝗵𝗶𝘀 𝗳𝘂𝗻𝗰𝘁𝗶𝗼𝗻 𝗶𝘀 𝗱𝗲𝘀𝗶𝗴𝗻𝗲𝗱 𝘁𝗼 𝗵𝗮𝗻𝗱𝗹𝗲 𝗱𝘂𝗿𝗮𝘁𝗶𝗼𝗻𝘀 𝗳𝗿𝗼𝗺 𝘀𝗲𝗰𝗼𝗻𝗱𝘀 𝘁𝗼 𝘄𝗲𝗲𝗸𝘀
 * 𝗍𝗈 𝗂𝗇𝖼𝗈𝗋𝗉𝗈𝗋𝖺𝗍𝖾 𝗆𝗈𝗇𝗍𝗁𝗌 𝖺𝗇𝖽 𝗒𝖾𝖺𝗋𝗌 𝗂𝗇𝗍𝗈 𝖼𝖺𝗅𝖼𝗎𝗅𝖺𝗍𝗂𝗈𝗇𝗌, 𝗐𝖾 𝗇𝖾𝖾𝖽 𝗆𝗈𝗋𝖾 𝗌𝗈𝗉𝗁𝗂𝗌𝗍𝗂𝖼𝖺𝗍𝖾𝖽 𝖼𝖺𝗅𝖾𝗇𝖽𝖺𝗋 𝖼𝗈𝗆𝗉𝗎𝗍𝖺𝗍𝗂𝗈𝗇𝗌.
 * 𝖠𝗌𝗎𝗆𝗂𝗇𝗀 𝟥𝟢 𝖽𝖺𝗒𝗌 𝗉𝖾𝗋 𝗆𝗈𝗇𝗍𝗁 𝖺𝗇𝖽 𝟥𝟨𝟧 𝖽𝖺𝗒𝗌 𝗉𝖾𝗋 𝗒𝖾𝖺𝗋, 𝖼𝗈𝗎𝗅𝖽 𝗅𝖾𝖺𝖽 𝗍𝗈 𝗆𝗂𝗇𝗈𝗋 𝗂𝗇𝖺𝖼𝖼𝗎𝗋𝖺𝖼𝗂𝖾𝗌 𝖻𝖾𝖼𝖺𝗎𝗌𝖾 𝗍𝗁𝖾 𝖦𝗋𝖾𝗀𝗈𝗋𝗂𝖺𝗇 𝖼𝖺𝗅𝖾𝗇𝖽𝖺𝗋,
 * 𝖼𝗈𝗇𝗍𝖺𝗂𝗇𝗌 𝗆𝗈𝗇𝗍𝗁𝗌 𝗍𝗁𝖺𝗍 𝖼𝖺𝗇 𝗁𝖺𝗏𝖾 𝟤𝟪, 𝟥𝟢, 𝗈𝗋 𝟥𝟣 𝖽𝖺𝗒𝗌, 𝖺𝗇𝖽 𝗌𝗈𝗆𝖾 𝗒𝖾𝖺𝗋𝗌 (𝗅𝖾𝖺𝗉 𝗒𝖾𝖺𝗋𝗌) 𝖼𝗈𝗇𝗌𝗂𝗌𝗍 𝗈𝖿 𝟥𝟨𝟨 𝖽𝖺𝗒𝗌.
 * 𝖶𝖾 𝖼𝖺𝗇 𝖾𝗇𝗁𝖺𝗇𝖼𝖾 𝗍𝗁𝖾 𝗉𝗋𝖾𝖼𝗂𝗌𝗂𝗈𝗇 𝗎𝗌𝗂𝗇𝗀 𝗍𝗁𝖾 𝖺𝗏𝖾𝗋𝖺𝗀𝖾 𝗅𝖾𝗇𝗀𝗍𝗁𝗌 𝗈𝖿 𝖺 𝗒𝖾𝖺𝗋 (𝟥𝟨𝟧.𝟤𝟧 𝖽𝖺𝗒𝗌) 𝖺𝗇𝖽 𝖺 𝗆𝗈𝗇𝗍𝗁 (𝟥𝟢.𝟦𝟦 𝖽𝖺𝗒𝗌) 𝗁𝗈𝗐𝖾𝗏𝖾𝗋,
 * 𝗐𝗁𝗂𝗅𝖾 𝗍𝗁𝖾𝗌𝖾 𝖺𝖽𝗃𝗎𝗌𝗍𝗆𝖾𝗇𝗍𝗌 𝖽𝗈 𝗉𝗋𝗈𝗏𝗂𝖽𝖾 𝗆𝗈𝗋𝖾 𝖺𝖼𝖼𝗎𝗋𝖺𝗍𝖾 𝗋𝖾𝗌𝗎𝗅𝗍𝗌, 𝗍𝗁𝖾𝗒 𝖺𝗋𝖾 𝗌𝗍𝗂𝗅𝗅 𝗇𝗈𝗍 𝖾𝗇𝗍𝗂𝗋𝖾𝗅𝗒 𝗉𝖾𝗋𝖿𝖾𝖼𝗍
 */
export const formatTimeDuration = (value, until, separator, shortFormat) => {
    const { floor } = Math;
    const parts = [];
    let remain = value;

    const shortUnitMap = {
        w: 'wk',
        d: 'day',
        h: 'hr',
        i: 'min',
        s: 'sec',
        ms: 'ms'
    };

    const units = shortFormat
        ? [
              // day = 86400000 = hour * 24
              ['d', 864e5],
              // hour = 3600000 = minute * 60
              ['h', 36e5],
              // minute = 60000 = second * 60
              ['i', 6e4],
              // second = 1000 = millisecond * 1000
              ['s', 1e3],
              // millisecond = 1
              ['ms', 1]
          ]
        : [
              // week = 604800000 = day * 7
              ['w', 6048e5],
              // day = 86400000 = hour * 24
              ['d', 864e5],
              // hour = 3600000 = minute * 60
              ['h', 36e5],
              // minute = 60000 = second * 60
              ['i', 6e4],
              // second = 1000 = millisecond * 1000
              ['s', 1e3]
          ];

    for (const [unit, modulo] of units) {
        const amount = floor(remain / modulo);
        if (amount) {
            if (shortFormat) {
                parts.push(`${amount}\u0020${shortUnitMap[unit]}`);
            } else {
                const obj =
                    dateUnitTranslationMap.get(unit)[
                        amount > 1
                            ? 1 // 𝘱𝘭𝘶𝘳𝘢𝘭
                            : 0 // 𝘴𝘪𝘯𝘨𝘶𝘭𝘢𝘳
                    ];
                const translation = translate(obj.message);
                parts.push(`${amount}\u0020${translation}`);
            }
            remain %= modulo;
        }
        if (until === unit) {
            break;
        }
    }
    return parts.join(separator || ',\u0020');
};

/**
 * Format a time duration into short human readable format with rounding.
 * Shows only the top 2 units and rounds the second unit based on the remainder.
 * Values below 1 second are displayed as decimal seconds.
 * @param {number} value - time duration in milliseconds.
 * @returns {string}
 * @example
 * formatTimeDurationShortRounded(103800000) // "1 day 5 hr" (1d 4h 50m rounded)
 * formatTimeDurationShortRounded(132536) // "2 min 13 sec" (2m 12s 536ms rounded)
 * formatTimeDurationShortRounded(500) // "0.50 sec"
 */
export const formatTimeDurationShortRounded = value => {
    // Validate input
    if (value == null || typeof value !== 'number' || isNaN(value)) {
        return '';
    }

    // Handle zero or negative values
    if (value <= 0) {
        return '0.00 sec';
    }

    // Handle infinity
    if (!isFinite(value)) {
        return '';
    }

    // If less than 1 second, show as decimal seconds
    if (value < 1e3) {
        const seconds = (value / 1e3).toFixed(2);
        return `${seconds} sec`;
    }

    const { floor, round } = Math;
    const units = [
        ['day', 864e5], // 86400000 ms
        ['hr', 36e5], // 3600000 ms
        ['min', 6e4], // 60000 ms
        ['sec', 1e3] // 1000 ms
    ];

    // Find the first non-zero unit (largest unit in the value)
    let firstUnitIndex = -1;
    for (let i = 0; i < units.length; i++) {
        if (value >= units[i][1]) {
            firstUnitIndex = i;
            break;
        }
    }

    // Determine rounding precision - we'll round to the second unit
    // This eliminates the need for complex overflow handling
    const secondUnitIndex = firstUnitIndex + 1;
    const roundingModulo =
        secondUnitIndex < units.length ? units[secondUnitIndex][1] : 1e3;

    // Round the input value first, then format it (key simplification!)
    const roundedValue = round(value / roundingModulo) * roundingModulo;

    // Now simply format the rounded value - calculate top 2 units
    let remain = roundedValue;
    const parts = [];

    for (let i = 0; i < units.length && parts.length < 2; i++) {
        const [unit, modulo] = units[i];
        const amount = floor(remain / modulo);

        if (amount > 0) {
            parts.push(`${amount} ${unit}`);
            remain %= modulo;
        }
    }

    return parts.length > 0 ? parts.join(' ') : '0.00 sec';
};

/**
 * Retrieves the appropriate translation object for a given time unit, either in singular or plural form.
 * @param {string} unit - The time unit (y|m|d|h|i|s).
 * @param {boolean} singular - Determines whether to return singular or plural form.
 * @returns {({id: string, message: string, defaultMessage: string}|false)}
 */
export const getDateUnitTranslationObj = (unit, singular) =>
    dateUnitTranslationMap.get(unit)?.[singular ? 0 : 1] || false;

/**
 * Map containing the mapping of PHP date format characters to date-fns format.
 * @type {Map<string, string>}
 */
const PHPToDateFnsMap = new Map(
    Object.entries({
        // Full month name 𝖾.𝗀.: '𝖩𝖺𝗇𝗎𝖺𝗋𝗒'
        F: 'MMMM',
        // Short month name 𝖾.𝗀.: '𝖩𝖺𝗇'
        M: 'LLL',
        // Short day name 𝖾.𝗀.: '𝖲𝗎𝗇'
        D: 'EEE',
        // 24-hour format 𝖾.𝗀.: 00-23
        H: 'HH',
        // Full year 𝖾.𝗀.: 2000
        Y: 'yyyy',
        // '𝖠𝖬'|'𝖯𝖬'
        a: 'a',
        // Zero-padded day 𝖾.𝗀.: 01-31
        d: 'dd',
        // Zero-padded hour 𝖾.𝗀.: 01-12 (𝟣𝟤-𝗁𝗈𝗎𝗋 𝖿𝗈𝗋𝗆𝖺𝗍)
        h: 'hh',
        // Zero-padded minute 𝖾.𝗀.: 00-59
        i: 'mm',
        // Full day name 𝖾.𝗀.: '𝖲𝗎𝗇𝖽𝖺𝗒'
        l: 'EEEE',
        // Zero-padded month 𝖾.𝗀.: 01-12
        m: 'MM',
        // Zero-padded second 𝖾.𝗀.: 00-59
        s: 'ss',
        // Short year 𝖾.𝗀.: '22' 𝖿𝗈𝗋 𝗍𝗁𝖾 𝗒𝖾𝖺𝗋 2022
        y: 'yy'
    })
);

/**
 * Converts a string from PHP-date format to a date-fns format.
 * @param {string} phpDate - The date string in PHP-date format.
 * @returns {string} - The date-fns format.
 * @throws {Error} when format character is missing in the mapping.
 */
export const fromPHPToDateFnsFormat = phpDate => {
    return phpDate.replace(RE.WordChars, char => {
        if (PHPToDateFnsMap.has(char)) {
            return PHPToDateFnsMap.get(char);
        } else {
            throw new Error(
                `Missing PHP format character '${char}' in PHPToDateFnsMap`
            );
        }
    });
};

/**
 * Converts a given conversion format to a date-fns format according to user settings.
 * @param {string} key - The conversion format key.
 * @returns {string}
 */
export const convertToUserSettingsDateFormat = key => {
    const settingsDateFormat = SETTINGS?.dateFormats?.[key];
    const conversionFormat = settingsDateFormat || 'm-d-y';
    // noinspection UnnecessaryLocalVariableJS
    const dateFnsFormat = fromPHPToDateFnsFormat(conversionFormat);
    // console.log({ key, settingsDateFormat, conversionFormat, dateFnsFormat });
    return dateFnsFormat;
};

/**
 * Formats a Unix timestamp or a Date object according to user settings.
 * @param {number|Date} value - unix timestamp or Date object.
 * @param {number} type - cf date format
 * @returns {string}
 */
export const formatTimeAsUserSettings = (value, type) => {
    const date = isDate(value) ? value : fromUnixTime(value);
    const formatKey = DATE === type ? longDateFormat : longDateTimeFormat;
    const settingsFormatValue = convertToUserSettingsDateFormat(formatKey);
    // console.log({ formatKey, settingsFormatValue });
    // noinspection UnnecessaryLocalVariableJS
    const formattedDate = format(date, settingsFormatValue);
    // console.log({ value, date, formattedDate });
    return formattedDate;
};

/**
 * Creates a Date object from a timestamp provided in seconds.
 * @param {number} timestamp in seconds.
 * @returns {Date}
 */
export const unixTimestampToDate = timestamp => {
    const ms = (timestamp || 0) * 1e3;
    return new Date(ms);
};

/**
 * Formats a Unix timestamp with a given date format.
 * @param {number} timestamp - The Unix timestamp in seconds.
 * @param {string} dateFormat - The desired date format.
 * @returns {string}
 */
export const formatUnixTimestamp = (timestamp, dateFormat) => {
    const dateObj = fromUnixTime(timestamp);
    return format(dateObj, dateFormat);
};

/**
 * Get the system timezone offset
 * @returns {number}
 */
export const getSystemTimezoneOffset = () => {
    return new Date().getTimezoneOffset();
};

/**
 * Removes the GMT offset from the given date.
 * @param {Date} date - The date object to remove GMT offset from.
 * @returns {Date} - The modified date object without GMT offset.
 */
export const removeGmtOffset = date => {
    return new Date(date.getTime() + date.getTimezoneOffset() * 60000);
};

/**
 * Formats a value to server-specific format (ISO string).
 * @param {number|Date} date - The input value, either a number representing timestamp or a Date object.
 * @returns {string|undefined}
 */
export const serverDateFormat = date => {
    const timestamp =
        date && (isNumber(date) ? date : (date.getTime?.() ?? 0) / 1e3);
    if (timestamp) {
        const msWithoutOffset =
            timestamp -
            getCurrentUserOffsetAsNum() -
            getSystemTimezoneOffset() * 60;
        const dateObj = unixTimestampToDate(msWithoutOffset);
        return dateObj.toISOString();
    }
};

/**
 * Converts a timestamp to a user-specified date format.
 * @param {number} timestamp - The timestamp to convert.
 * @param {string} format - The format to apply.
 * @param {boolean} [force] - If true, ignore user settings and apply the format argument.
 * @returns {string} formatted date-time
 */
export const userDateFormat = (timestamp, format, force) => {
    let formattedValue = '';
    if (timestamp) {
        // prettier-ignore
        const dateFormat = force ? format : convertToUserSettingsDateFormat(format);
        const userOffset = getCurrentUserOffsetAsNum();
        const systemOffset = getSystemTimezoneOffset() * 60;
        // console.log({ userOffset, systemOffset });
        const value = timestamp + userOffset + systemOffset;
        formattedValue = formatUnixTimestamp(value, dateFormat);
        // console.log(dateFormat, { value, formattedValue });
    }
    return formattedValue;
};

export const formatAsUserShortDate = (timestamp, force) => {
    return userDateFormat(timestamp, shortDateFormat, force);
};

export const formatAsUserShortDateTime = (timestamp, force) => {
    return userDateFormat(timestamp, shortDateTimeFormat, force);
};

export const formatAsUserLongDate = (timestamp, force) => {
    return userDateFormat(timestamp, longDateFormat, force);
};

export const formatAsUserLongDateTime = (timestamp, force) => {
    return userDateFormat(timestamp, longDateTimeFormat, force);
};

/*
(h|H):i(:s( a))

┌───────────────┐┌───────────────┐
│ h:i a         ││ h:i:s a       │
│ hh:mm a       ││ hh:mm:ss a    │
│ 12:00 AM      ││ 12:00:00 AM   │
│ 01:46 PM      ││ 01:46:00 PM   │
└───────────────┘└───────────────┘
┌───────────────┐┌───────────────┐
│ H:i           ││ H:i:s         │
│ HH:mm         ││ HH:mm:ss      │
│ 00:00         ││ 00:00:00      │
│ 13:46         ││ 13:46:00      │
└───────────────┘└───────────────┘

*/
/*

y-m-d
┌───────────────┐┌───────────────┐
│ y-m-d h:i a   ││ Y-m-d h:i a   │
│ y-m-d h:i:s a ││ Y-m-d h:i:s a │
│ y-m-d H:i     ││ Y-m-d H:i     │
│ y-m-d H:i:s   ││ Y-m-d H:i:s   │
└───────────────┘└───────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ y-m-d h:i a                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ y-m-d           │ Y-m-d           │ y-m-d h:i a               │ Y-m-d h:i a                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ yy-MM-dd        │ yyyy-MM-dd      │ yy-MM-dd hh:mm a          │ yyyy-MM-dd hh:mm a          │
│ 70-01-01        │ 1970-01-01      │ 70-01-01 12:00 AM         │ 1970-01-01 12:00 AM         │
│ 21-03-10        │ 2021-03-10      │ 21-03-10 01:46 PM         │ 2021-03-10 01:46 PM         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ y-m-d h:i:s a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ y-m-d           │ Y-m-d           │ y-m-d h:i:s a             │ Y-m-d h:i:s a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ yy-MM-dd        │ yyyy-MM-dd      │ yy-MM-dd hh:mm:ss a       │ yyyy-MM-dd hh:mm:ss a       │
│ 70-01-01        │ 1970-01-01      │ 70-01-01 12:00:00 AM      │ 1970-01-01 12:00:00 AM      │
│ 21-03-10        │ 2021-03-10      │ 21-03-10 01:46:00 PM      │ 2021-03-10 01:46:00 PM      │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ y-m-d H:i                                                                                   │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ y-m-d           │ Y-m-d           │ y-m-d H:i                 │ Y-m-d H:i                   │
│-----------------│-----------------│---------------------------│-----------------------------│
│ yy-MM-dd        │ yyyy-MM-dd      │ yy-MM-dd HH:mm            │ yyyy-MM-dd HH:mm            │
│ 70-01-01        │ 1970-01-01      │ 70-01-01 00:00            │ 1970-01-01 00:00            │
│ 21-03-10        │ 2021-03-10      │ 21-03-10 13:46            │ 2021-03-10 13:46            │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ y-m-d H:i:s                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ y-m-d           │ Y-m-d           │ y-m-d H:i:s               │ Y-m-d H:i:s                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ yy-MM-dd        │ yyyy-MM-dd      │ yy-MM-dd HH:mm:ss         │ yyyy-MM-dd HH:mm:ss         │
│ 70-01-01        │ 1970-01-01      │ 70-01-01 00:00:00         │ 1970-01-01 00:00:00         │
│ 21-03-10        │ 2021-03-10      │ 21-03-10 13:46:00         │ 2021-03-10 13:46:00         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
*/
/*

m-d-y
┌───────────────┐┌───────────────┐
│ m-d-y h:i a   ││ m-d-Y h:i a   │
│ m-d-y h:i:s a ││ m-d-Y h:i:s a │
│ m-d-y H:i     ││ m-d-Y H:i     │
│ m-d-y H:i:s   ││ m-d-Y H:i:s   │
└───────────────┘└───────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m-d-y h:i a                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m-d-y           │ m-d-Y           │ m-d-y h:i a               │ m-d-Y h:i a                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM-dd-yy        │ MM-dd-yyyy      │ MM-dd-yy hh:mm a          │ MM-dd-yyyy hh:mm a          │
│ 01-01-70        │ 01-01-1970      │ 01-01-70 12:00 AM         │ 01-01-1970 12:00 AM         │
│ 03-10-21        │ 03-10-2021      │ 03-10-21 01:46 PM         │ 03-10-2021 01:46 PM         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m-d-y h:i:s a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m-d-y           │ m-d-Y           │ m-d-y h:i:s a             │ m-d-Y h:i:s a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM-dd-yy        │ MM-dd-yyyy      │ MM-dd-yy hh:mm:ss a       │ MM-dd-yyyy hh:mm:ss a       │
│ 01-01-70        │ 01-01-1970      │ 01-01-70 12:00:00 AM      │ 01-01-1970 12:00:00 AM      │
│ 03-10-21        │ 03-10-2021      │ 03-10-21 01:46:00 PM      │ 03-10-2021 01:46:00 PM      │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m-d-y H:i                                                                                   │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m-d-y           │ m-d-Y           │ m-d-y H:i                 │ m-d-Y H:i                   │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM-dd-yy        │ MM-dd-yyyy      │ MM-dd-yy HH:mm            │ MM-dd-yyyy HH:mm            │
│ 01-01-70        │ 01-01-1970      │ 01-01-70 00:00            │ 01-01-1970 00:00            │
│ 03-10-21        │ 03-10-2021      │ 03-10-21 13:46            │ 03-10-2021 13:46            │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m-d-y H:i:s                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m-d-y           │ m-d-Y           │ m-d-y H:i:s               │ m-d-Y H:i:s                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM-dd-yy        │ MM-dd-yyyy      │ MM-dd-yy HH:mm:ss         │ MM-dd-yyyy HH:mm:ss         │
│ 01-01-70        │ 01-01-1970      │ 01-01-70 00:00:00         │ 01-01-1970 00:00:00         │
│ 03-10-21        │ 03-10-2021      │ 03-10-21 13:46:00         │ 03-10-2021 13:46:00         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
*/
/*

d-m-y
┌───────────────┐┌───────────────┐
│ d-m-y h:i a   ││ d-m-Y h:i a   │
│ d-m-y h:i:s a ││ d-m-Y h:i:s a │
│ d-m-y H:i     ││ d-m-Y H:i     │
│ d-m-y H:i:s   ││ d-m-Y H:i:s   │
└───────────────┘└───────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ d-m-y h:i a                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ d-m-y           │ d-m-Y           │ d-m-y h:i a               │ d-m-Y h:i a                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ dd-MM-yy        │ dd-MM-yyyy      │ dd-MM-yy hh:mm a          │ dd-MM-yyyy hh:mm a          │
│ 01-01-70        │ 01-01-1970      │ 01-01-70 12:00 AM         │ 01-01-1970 12:00 AM         │
│ 10-03-21        │ 10-03-2021      │ 10-03-21 01:46 PM         │ 10-03-2021 01:46 PM         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ d-m-y h:i:s a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ d-m-y           │ d-m-Y           │ d-m-y h:i:s a             │ d-m-Y h:i:s a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ dd-MM-yy        │ dd-MM-yyyy      │ dd-MM-yy hh:mm:ss a       │ dd-MM-yyyy hh:mm:ss a       │
│ 01-01-70        │ 01-01-1970      │ 01-01-70 12:00:00 AM      │ 01-01-1970 12:00:00 AM      │
│ 10-03-21        │ 10-03-2021      │ 10-03-21 01:46:00 PM      │ 10-03-2021 01:46:00 PM      │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ d-m-y H:i                                                                                   │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ d-m-y           │ d-m-Y           │ d-m-y H:i                 │ d-m-Y H:i                   │
│-----------------│-----------------│---------------------------│-----------------------------│
│ dd-MM-yy        │ dd-MM-yyyy      │ dd-MM-yy HH:mm            │ dd-MM-yyyy HH:mm            │
│ 01-01-70        │ 01-01-1970      │ 01-01-70 00:00            │ 01-01-1970 00:00            │
│ 10-03-21        │ 10-03-2021      │ 10-03-21 13:46            │ 10-03-2021 13:46            │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ d-m-y H:i:s                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ d-m-y           │ d-m-Y           │ d-m-y H:i:s               │ d-m-Y H:i:s                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ dd-MM-yy        │ dd-MM-yyyy      │ dd-MM-yy HH:mm:ss         │ dd-MM-yyyy HH:mm:ss         │
│ 01-01-70        │ 01-01-1970      │ 01-01-70 00:00:00         │ 01-01-1970 00:00:00         │
│ 10-03-21        │ 10-03-2021      │ 10-03-21 13:46:00         │ 10-03-2021 13:46:00         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
*/
/*

y/m/d
┌───────────────┐┌───────────────┐
│ y/m/d h:i a   ││ Y/m/d h:i a   │
│ y/m/d h:i:s a ││ Y/m/d h:i:s a │
│ y/m/d H:i     ││ Y/m/d H:i     │
│ y/m/d H:i:s   ││ Y/m/d H:i:s   │
└───────────────┘└───────────────┘
*/
/*

m/d/y
┌───────────────┐┌───────────────┐
│ m/d/y h:i a   ││ m/d/Y h:i a   │
│ m/d/y h:i:s a ││ m/d/Y h:i:s a │
│ m/d/y H:i     ││ m/d/Y H:i     │
│ m/d/y H:i:s   ││ m/d/Y H:i:s   │
└───────────────┘└───────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m/d/y h:i a                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m/d/y           │ m/d/Y           │ m/d/y h:i a               │ m/d/Y h:i a                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM/dd/yy        │ MM/dd/yyyy      │ MM/dd/yy hh:mm a          │ MM/dd/yyyy hh:mm a          │
│ 01/01/70        │ 01/01/1970      │ 01/01/70 12:00 AM         │ 01/01/1970 12:00 AM         │
│ 03/10/21        │ 03/10/2021      │ 03/10/21 01:46 PM         │ 03/10/2021 01:46 PM         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m/d/y h:i:s a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m/d/y           │ m/d/Y           │ m/d/y h:i:s a             │ m/d/Y h:i:s a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM/dd/yy        │ MM/dd/yyyy      │ MM/dd/yy hh:mm:ss a       │ MM/dd/yyyy hh:mm:ss a       │
│ 01/01/70        │ 01/01/1970      │ 01/01/70 12:00:00 AM      │ 01/01/1970 12:00:00 AM      │
│ 03/10/21        │ 03/10/2021      │ 03/10/21 01:46:00 PM      │ 03/10/2021 01:46:00 PM      │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m/d/y H:i                                                                                   │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m/d/y           │ m/d/Y           │ m/d/y H:i                 │ m/d/Y H:i                   │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM/dd/yy        │ MM/dd/yyyy      │ MM/dd/yy HH:mm            │ MM/dd/yyyy HH:mm            │
│ 01/01/70        │ 01/01/1970      │ 01/01/70 00:00            │ 01/01/1970 00:00            │
│ 03/10/21        │ 03/10/2021      │ 03/10/21 13:46            │ 03/10/2021 13:46            │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m/d/y H:i:s                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m/d/y           │ m/d/Y           │ m/d/y H:i:s               │ m/d/Y H:i:s                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM/dd/yy        │ MM/dd/yyyy      │ MM/dd/yy HH:mm:ss         │ MM/dd/yyyy HH:mm:ss         │
│ 01/01/70        │ 01/01/1970      │ 01/01/70 00:00:00         │ 01/01/1970 00:00:00         │
│ 03/10/21        │ 03/10/2021      │ 03/10/21 13:46:00         │ 03/10/2021 13:46:00         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
*/
/*

d/m/y
┌───────────────┐┌───────────────┐
│ d/m/y h:i a   ││ d/m/Y h:i a   │
│ d/m/y h:i:s a ││ d/m/Y h:i:s a │
│ d/m/y H:i     ││ d/m/Y H:i     │
│ d/m/y H:i:s   ││ d/m/Y H:i:s   │
└───────────────┘└───────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ d/m/y h:i a                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ d/m/y           │ d/m/Y           │ d/m/y h:i a               │ d/m/Y h:i a                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ dd/MM/yy        │ dd/MM/yyyy      │ dd/MM/yy hh:mm a          │ dd/MM/yyyy hh:mm a          │
│ 01/01/70        │ 01/01/1970      │ 01/01/70 12:00 AM         │ 01/01/1970 12:00 AM         │
│ 10/03/21        │ 10/03/2021      │ 10/03/21 01:46 PM         │ 10/03/2021 01:46 PM         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ d/m/y h:i:s a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ d/m/y           │ d/m/Y           │ d/m/y h:i:s a             │ d/m/Y h:i:s a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ dd/MM/yy        │ dd/MM/yyyy      │ dd/MM/yy hh:mm:ss a       │ dd/MM/yyyy hh:mm:ss a       │
│ 01/01/70        │ 01/01/1970      │ 01/01/70 12:00:00 AM      │ 01/01/1970 12:00:00 AM      │
│ 10/03/21        │ 10/03/2021      │ 10/03/21 01:46:00 PM      │ 10/03/2021 01:46:00 PM      │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ d/m/y H:i                                                                                   │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ d/m/y           │ d/m/Y           │ d/m/y H:i                 │ d/m/Y H:i                   │
│-----------------│-----------------│---------------------------│-----------------------------│
│ dd/MM/yy        │ dd/MM/yyyy      │ dd/MM/yy HH:mm            │ dd/MM/yyyy HH:mm            │
│ 01/01/70        │ 01/01/1970      │ 01/01/70 00:00            │ 01/01/1970 00:00            │
│ 10/03/21        │ 10/03/2021      │ 10/03/21 13:46            │ 10/03/2021 13:46            │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ d/m/y H:i:s                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ d/m/y           │ d/m/Y           │ d/m/y H:i:s               │ d/m/Y H:i:s                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ dd/MM/yy        │ dd/MM/yyyy      │ dd/MM/yy HH:mm:ss         │ dd/MM/yyyy HH:mm:ss         │
│ 01/01/70        │ 01/01/1970      │ 01/01/70 00:00:00         │ 01/01/1970 00:00:00         │
│ 10/03/21        │ 10/03/2021      │ 10/03/21 13:46:00         │ 10/03/2021 13:46:00         │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
*/
/*

y m D d
┌─────────────────┐┌─────────────────┐
│ y m D d h:i a   ││ Y m D d h:i a   │
│ y m D d h:i:s a ││ Y m D d h:i:s a │
│ y m D d H:i     ││ Y m D d H:i     │
│ y m D d H:i:s   ││ Y m D d H:i:s   │
└─────────────────┘└─────────────────┘
*/
/*

y M D d
┌─────────────────┐┌─────────────────┐
│ y M D d h:i a   ││ Y M D d h:i a   │
│ y M D d h:i:s a ││ Y M D d h:i:s a │
│ y M D d H:i     ││ Y M D d H:i     │
│ y M D d H:i:s   ││ Y M D d H:i:s   │
└─────────────────┘└─────────────────┘
*/
/*

y D m d
┌─────────────────┐┌─────────────────┐
│ y D m d h:i a   ││ Y D m d h:i a   │
│ y D m d h:i:s a ││ Y D m d h:i:s a │
│ y D m d H:i     ││ Y D m d H:i     │
│ y D m d H:i:s   ││ Y D m d H:i:s   │
└─────────────────┘└─────────────────┘
*/
/*

y D M d
┌─────────────────┐┌─────────────────┐
│ y D M d h:i a   ││ Y D M d h:i a   │
│ y D M d h:i:s a ││ Y D M d h:i:s a │
│ y D M d H:i     ││ Y D M d H:i     │
│ y D M d H:i:s   ││ Y D M d H:i:s   │
└─────────────────┘└─────────────────┘
*/
/*

m D d y
┌─────────────────┐┌─────────────────┐
│ m D d y h:i a   ││ m D d Y h:i a   │
│ m D d y h:i:s a ││ m D d Y h:i:s a │
│ m D d y H:i     ││ m D d Y H:i     │
│ m D d y H:i:s   ││ m D d Y H:i:s   │
└─────────────────┘└─────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m D d y h:i a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m D d y         │ m D d Y         │ m D d y h:i a             │ m D d Y h:i a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM EEE dd yy    │ MM EEE dd yyyy  │ MM EEE dd yy hh:mm a      │ MM EEE dd yyyy hh:mm a      │
│ 01 Thu 01 70    │ 01 Thu 01 1970  │ 01 Thu 01 70 12:00 AM     │ 01 Thu 01 1970 12:00 AM     │
│ 03 Wed 10 21    │ 03 Wed 10 2021  │ 03 Wed 10 21 01:46 PM     │ 03 Wed 10 2021 01:46 PM     │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m D d y h:i:s a                                                                             │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m D d y         │ m D d Y         │ m D d y h:i:s a           │ m D d Y h:i:s a             │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM EEE dd y     │ MM EEE dd yyyy  │ MM EEE dd yy hh:mm:ss a   │ MM EEE dd yyyy hh:mm:ss a   │
│ 01 Thu 01 70    │ 01 Thu 01 1970  │ 01 Thu 01 70 12:00:00 AM  │ 01 Thu 01 1970 12:00:00 AM  │
│ 03 Wed 10 21    │ 03 Wed 10 2021  │ 03 Wed 10 21 01:46:00 PM  │ 03 Wed 10 2021 01:46:00 PM  │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m D d y H:i                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m D d y         │ m D d Y         │ m D d y H:i               │ m D d Y H:i                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM EEE dd y     │ MM EEE dd yyyy  │ MM EEE dd yy HH:mm        │ MM EEE dd yyyy HH:mm        │
│ 01 Thu 01 70    │ 01 Thu 01 1970  │ 01 Thu 01 70 00:00        │ 01 Thu 01 1970 00:00        │
│ 03 Wed 10 21    │ 03 Wed 10 2021  │ 03 Wed 10 21 13:46        │ 03 Wed 10 2021 13:46        │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ m D d y H:i:s                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ m D d y         │ m D d Y         │ m D d y H:i:s             │ m D d Y H:i:s               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ MM EEE dd yy    │ MM EEE dd yyyy  │ MM EEE dd yy HH:mm:ss     │ MM EEE dd yyyy HH:mm:ss     │
│ 01 Thu 01 70    │ 01 Thu 01 1970  │ 01 Thu 01 70 00:00:00     │ 01 Thu 01 1970 00:00:00     │
│ 03 Wed 10 21    │ 03 Wed 10 2021  │ 03 Wed 10 21 13:46:00     │ 03 Wed 10 2021 13:46:00     │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
*/
/*

M D d y
┌─────────────────┐┌─────────────────┐
│ M D d y h:i a   ││ M D d Y h:i a   │
│ M D d y h:i:s a ││ M D d Y h:i:s a │
│ M D d y H:i     ││ M D d Y H:i     │
│ M D d y H:i:s   ││ M D d Y H:i:s   │
└─────────────────┘└─────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ M D d y h:i a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ M D d y         │ M D d Y         │ M D d y h:i a             │ M D d Y h:i a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ LLL EEE dd yy   │ LLL EEE dd yyyy │ LLL EEE dd yy hh:mm a     │ LLL EEE dd yyyy hh:mm a     │
│ Jan Thu 01 70   │ Jan Thu 01 1970 │ Jan Thu 01 70 12:00 AM    │ Jan Thu 01 1970 12:00 AM    │
│ Mar Wed 10 21   │ Mar Wed 10 2021 │ Mar Wed 10 21 01:46 PM    │ Mar Wed 10 2021 01:46 PM    │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ M D d y h:i:s a                                                                             │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ M D d y         │ M D d Y         │ M D d y h:i:s a           │ M D d Y h:i:s a             │
│-----------------│-----------------│---------------------------│-----------------------------│
│ LLL EEE dd yy   │ LLL EEE dd yyyy │ LLL EEE dd yy hh:mm:ss a  │ LLL EEE dd yyyy hh:mm:ss a  │
│ Jan Thu 01 70   │ Jan Thu 01 1970 │ Jan Thu 01 70 12:00:00 AM │ Jan Thu 01 1970 12:00:00 AM │
│ Mar Wed 10 21   │ Mar Wed 10 2021 │ Mar Wed 10 21 01:46:00 PM │ Mar Wed 10 2021 01:46:00 PM │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ M D d y H:i                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ M D d y         │ M D d Y         │ M D d y H:i               │ M D d Y H:i                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ LLL EEE dd yy   │ LLL EEE dd yyyy │ LLL EEE dd yy HH:mm       │ LLL EEE dd yyyy HH:mm       │
│ Jan Thu 01 70   │ Jan Thu 01 1970 │ Jan Thu 01 70 00:00       │ Jan Thu 01 1970 00:00       │
│ Mar Wed 10 21   │ Mar Wed 10 2021 │ Mar Wed 10 21 13:46       │ Mar Wed 10 2021 13:46       │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ M D d y H:i:s                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ M D d y         │ M D d Y         │ M D d y H:i:s             │ M D d Y H:i:s               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ LLL EEE dd yy   │ LLL EEE dd yyyy │ LLL EEE dd yy HH:mm:ss    │ LLL EEE dd yyyy HH:mm:ss    │
│ Jan Thu 01 70   │ Jan Thu 01 1970 │ Jan Thu 01 70 00:00:00    │ Jan Thu 01 1970 00:00:00    │
│ Mar Wed 10 21   │ Mar Wed 10 2021 │ Mar Wed 10 21 13:46:00    │ Mar Wed 10 2021 13:46:00    │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
*/
/*

D m d y
┌─────────────────┐┌─────────────────┐
│ D m d y h:i a   ││ D m d Y h:i a   │
│ D m d y h:i:s a ││ D m d Y h:i:s a │
│ D m d y H:i     ││ D m d Y H:i     │
│ D m d y H:i:s   ││ D m d Y H:i:s   │
└─────────────────┘└─────────────────┘

*/
/*

D M d y
┌─────────────────┐┌─────────────────┐
│ D M d y h:i a   ││ D M d Y h:i a   │
│ D M d y h:i:s a ││ D M d Y h:i:s a │
│ D M d y H:i     ││ D M d Y H:i     │
│ D M d y H:i:s   ││ D M d Y H:i:s   │
└─────────────────┘└─────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ D M d y h:i a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ D M d y         │ D M d Y         │ D M d y h:i a             │ D M d Y h:i a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ EEE LLL dd yy   │ EEE LLL dd yyyy │ EEE LLL dd yy hh:mm a     │ EEE LLL dd yyyy hh:mm a     │
│ Thu Jan 01 70   │ Thu Jan 01 1970 │ Thu Jan 01 70 12:00 AM    │ Thu Jan 01 1970 12:00 AM    │
│ Wed Mar 10 21   │ Wed Mar 10 2021 │ Wed Mar 10 21 01:46 PM    │ Wed Mar 10 2021 01:46 PM    │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ D M d y h:i:s a                                                                             │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ D M d y         │ D M d Y         │ D M d y h:i:s a           │ D M d Y h:i:s a             │
│-----------------│-----------------│---------------------------│-----------------------------│
│ EEE LLL dd yy   │ EEE LLL dd yyyy │ EEE LLL dd yy hh:mm:ss a  │ EEE LLL dd yyyy hh:mm:ss a  │
│ Thu Jan 01 70   │ Thu Jan 01 1970 │ Thu Jan 01 70 12:00:00 AM │ Thu Jan 01 1970 12:00:00 AM │
│ Wed Mar 10 21   │ Wed Mar 10 2021 │ Wed Mar 10 21 01:46:00 PM │ Wed Mar 10 2021 01:46:00 PM │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ D M d y H:i                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ D M d y         │ D M d Y         │ D M d y H:i               │ D M d Y H:i                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ EEE LLL dd yy   │ EEE LLL dd yyyy │ EEE LLL dd yy HH:mm       │ EEE LLL dd yyyy HH:mm       │
│ Thu Jan 01 70   │ Thu Jan 01 1970 │ Thu Jan 01 70 00:00       │ Thu Jan 01 1970 00:00       │
│ Wed Mar 10 21   │ Wed Mar 10 2021 │ Wed Mar 10 21 13:46       │ Wed Mar 10 2021 13:46       │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ D M d y H:i:s                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ D M d y         │ D M d Y         │ D M d y H:i:s             │ D M d Y H:i:s               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ EEE LLL dd yy   │ EEE LLL dd yyyy │ EEE LLL dd yy HH:mm:ss    │ EEE LLL dd yyyy HH:mm:ss    │
│ Thu Jan 01 70   │ Thu Jan 01 1970 │ Thu Jan 01 70 00:00:00    │ Thu Jan 01 1970 00:00:00    │
│ Wed Mar 10 21   │ Wed Mar 10 2021 │ Wed Mar 10 21 13:46:00    │ Wed Mar 10 2021 13:46:00    │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
 */
/*

D d m y
┌─────────────────┐┌─────────────────┐
│ D d m y h:i a   ││ D d m Y h:i a   │
│ D d m y h:i:s a ││ D d m Y h:i:s a │
│ D d m y H:i     ││ D d m Y H:i     │
│ D d m y H:i:s   ││ D d m Y H:i:s   │
└─────────────────┘└─────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ D d m y h:i a                                                                               │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ D d m y         │ D d m Y         │ D d m y h:i a             │ D d m Y h:i a               │
│-----------------│-----------------│---------------------------│-----------------------------│
│ EEE dd MM yy    │ EEE dd MM yyyy  │ EEE dd MM yy hh:mm a      │ EEE dd MM yyyy hh:mm a      │
│ Thu 01 01 70    │ Thu 01 01 1970  │ Thu 01 01 70 12:00 AM     │ Thu 01 01 1970 12:00 AM     │
│ Wed 10 03 21    │ Wed 10 03 2021  │ Wed 10 03 21 01:46 PM     │ Wed 10 03 2021 01:46 PM     │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ D d m y h:i:s a                                                                             │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ D d m y         │ D d m Y         │ D d m y h:i:s a           │ D d m Y h:i:s a             │
│-----------------│-----------------│---------------------------│-----------------------------│
│ EEE dd MM yy    │ EEE dd MM yyyy  │ EEE dd MM yy hh:mm:ss a   │ EEE dd MM yyyy hh:mm:ss a   │
│ Thu 01 01 70    │ Thu 01 01 1970  │ Thu 01 01 70 12:00:00 AM  │ Thu 01 01 1970 12:00:00 AM  │
│ Wed 10 03 21    │ Wed 10 03 2021  │ Wed 10 03 21 01:46:00 PM  │ Wed 10 03 2021 01:46:00 PM  │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
┌─────────────────────────────────────────────────────────────────────────────────────────────┐
│ D d m y H:i                                                                                 │
├─────────────────┬─────────────────┬───────────────────────────┬─────────────────────────────┤
│ shortDate       │ longDate        │ shortDateTime             │ longDateTime                │
│ D d m y         │ D d m Y         │ D d m y H:i               │ D d m Y H:i                 │
│-----------------│-----------------│---------------------------│-----------------------------│
│ EEE dd MM yy    │ EEE dd MM yyyy  │ EEE dd MM yy HH:mm        │ EEE dd MM yyyy HH:mm        │
│ Thu 01 01 70    │ Thu 01 01 1970  │ Thu 01 01 70 00:00        │ Thu 01 01 1970 00:00        │
│ Wed 10 03 21    │ Wed 10 03 2021  │ Wed 10 03 21 13:46        │ Wed 10 03 2021 13:46        │
└─────────────────┴─────────────────┴───────────────────────────┴─────────────────────────────┘
*/
/*

D d M y
┌─────────────────┐┌─────────────────┐
│ D d M y h:i a   ││ D d M Y h:i a   │
│ D d M y h:i:s a ││ D d M Y h:i:s a │
│ D d M y H:i     ││ D d M Y H:i     │
│ D d M y H:i:s   ││ D d M Y H:i:s   │
└─────────────────┘└─────────────────┘
*/
